import { Building2, Globe, Package, RefreshCw, ShieldCheck } from 'lucide-react'
import { PageHeader } from '@/components/shared/PageHeader'
import { Loader } from '@/components/shared/Loader'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useProductos } from '@/features/productos/hooks'
import { ProductoCard } from '@/features/productos/ProductoCard'
import { useAppStore } from '@/store/appStore'

export function ProductosPage() {
  const { data, loading, error, refetch } = useProductos()
  const { identity } = useAppStore()

  // Rol más relevante del usuario en Portal Empresa
  const peRole = Object.keys(useAppStore.getState().roles.map).find(
    (r) => r.startsWith('PE_') || r === 'CUSTOMER_ADMINISTRATOR',
  )

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ofertas"
        description="Administra y accede a todas las ofertas disponibles para tu organización."
        action={
          <Button variant="secondary" size="sm" onClick={() => void refetch()} disabled={loading}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </Button>
        }
      />

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader message="Cargando ofertas..." />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
          <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border-secondary bg-bg-primary py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-error-secondary">
              <Package size={22} className="text-text-error" />
            </div>
            <div>
              <p className="font-medium text-text-primary">No se pudo cargar las ofertas</p>
              <p className="mt-1 text-sm text-text-tertiary">{error}</p>
            </div>
          <Button variant="secondary" size="sm" onClick={() => void refetch()}>
            Reintentar
          </Button>
        </div>
      )}

      {/* Contenido */}
      {!loading && !error && data && (
        <div className="space-y-6">
          {/* Banner de organización */}
          <div className="flex flex-wrap items-center gap-4 rounded-xl border border-border-secondary bg-bg-primary px-5 py-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-bg-brand-solid">
              <Building2 size={18} className="text-fg-white" />
            </div>

            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold leading-tight text-text-primary">
                {data.orgName ?? 'Mi Organización'}
              </p>
              {data.orgPartyPublicId && (
                <div className="mt-0.5 flex items-center gap-1.5">
                  <Globe size={11} className="text-text-quaternary" />
                  <code className="text-[10px] font-mono text-text-tertiary">
                    {data.orgPartyPublicId}
                  </code>
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {peRole && (
                <Badge variant="outline" className="gap-1.5">
                  <ShieldCheck size={11} />
                  {peRole.replace(/_/g, ' ')}
                </Badge>
              )}
              {identity.isKashioSuper && (
                <Badge variant="warning" className="gap-1.5">
                  Kashio Super
                </Badge>
              )}
            </div>
          </div>

          {/* Grid de ofertas: solo muestra productos que hicieron match en KBRM
              (tienen offering_name) y no son este portal ni están embebidos en el sidebar */}
          {(() => {
            const external = data.products.filter(
              (p) => !p.isCurrentPortal && !p.isEmbedded && p.offering_name !== null,
            )
            return external.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border-secondary bg-bg-primary py-16 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-bg-secondary">
                  <Package size={22} className="text-text-quaternary" />
                </div>
                <p className="text-sm text-text-tertiary">
                  No tienes ofertas externas asignadas.
                </p>
                <p className="text-xs text-text-quaternary">
                  Las ofertas contratadas con acceso a portales adicionales aparecerán aquí.
                </p>
              </div>
            ) : (
              <div>
                <p className="mb-4 text-sm font-medium text-text-tertiary">
                  {external.length} oferta{external.length !== 1 ? 's' : ''} disponible
                  {external.length !== 1 ? 's' : ''}
                </p>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {external.map((product) => (
                    // Key compuesta: assetId puede repetirse en v3.0 (permission_public_id)
                    <ProductoCard key={`${product.asset.id}|${product.asset.name}`} product={product} />
                  ))}
                </div>
              </div>
            )
          })()}
        </div>
      )}

      {/* Sin datos (primer render antes de cargar) */}
      {!loading && !error && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader message="Inicializando..." />
        </div>
      )}
    </div>
  )
}
